/**
 * SurfaceBadgeStash — the member's badge collection.
 *
 * ## Why a collection needs a room
 *
 * A count beside a name says HOW MANY; it cannot say which, or what any of
 * them was for. Recognition that a member cannot look at is a number, and a
 * programme that rewards people with badges has to give them somewhere to be
 * looked at.
 *
 * ## Locked slots are the point
 *
 * A stash showing only what has been earned is a trophy case; a stash showing
 * the whole ladder with the unearned ones dim is a map. The second is what
 * makes a member come back for the third find, so the caller can pass the full
 * catalogue and this dims what is not held.
 *
 * Passing no catalogue is complete on its own — the member sees what they have
 * — rather than a degraded version of something else.
 */

import React from "react";
import type {MemberBadge} from "@/hooks/useGetMyBadges";
import "./SurfaceBadgeStash.scss";

/** One rung of the ladder, earned or not. */
export interface BadgeSlot {
    trackerId: string;
    name: string;
    imageUrl?: string;
    /** What earns it — "Post 10 finds". Shown on the locked ones especially. */
    requirement?: string;
}

export interface SurfaceBadgeStashProps {
    badges: readonly MemberBadge[];
    /**
     * Every badge in the programme, in ladder order. Anything not held renders
     * dim with its requirement, so the stash says what is still out there.
     */
    catalogue?: readonly BadgeSlot[];
    title?: string;
    /** Shown when the member has none and no catalogue was supplied. */
    emptyLabel?: string;
}

export const SurfaceBadgeStash: React.FC<SurfaceBadgeStashProps> = ({
    badges, catalogue, title = "Badges", emptyLabel = "No badges yet.",
}) => {
    const held = new Map(badges.map(b => [b.trackerId, b]));

    /*
     * The catalogue is the ladder when there is one; otherwise the member's own
     * badges ARE the list. Either way this is one array of slots, so the grid
     * below never branches on which case it is in.
     */
    const slots: BadgeSlot[] = catalogue?.length
        ? [...catalogue]
        : badges.map(b => ({trackerId: b.trackerId, name: b.name, imageUrl: b.imageUrl}));

    if (!slots.length) {
        return (
            <div className="sh-stash">
                <div className="sh-stash__hd">{title}</div>
                <p className="sh-stash__empty">{emptyLabel}</p>
            </div>
        );
    }

    return (
        <div className="sh-stash">
            <div className="sh-stash__hd">
                {title}
                <b>{badges.length}{catalogue?.length ? ` / ${catalogue.length}` : ""}</b>
            </div>

            <div className="sh-stash__grid">
                {slots.map(slot => {
                    const owned = held.get(slot.trackerId);
                    const image = owned?.imageUrl ?? slot.imageUrl;
                    return (
                        <div
                            key={slot.trackerId}
                            className={`sh-stash__b${owned ? "" : " sh-stash__b--locked"}`}
                            /*
                             * The requirement is the title on a LOCKED slot and
                             * the name on an earned one — on a locked badge
                             * "Post 10 finds" is the useful sentence, and on an
                             * earned one it is history.
                             */
                            title={owned ? slot.name : slot.requirement ?? slot.name}
                        >
                            <span className="sh-stash__art">
                                {image
                                    ? <img src={image} alt="" loading="lazy" />
                                    /* No artwork: the initial, not a broken frame. */
                                    : <i>{slot.name.slice(0, 1)}</i>}
                                {/*
                                  * More than one of the same badge — earned
                                  * again on a later week. Shown as a multiple
                                  * rather than as a second tile, which would
                                  * make one achievement look like two.
                                  */}
                                {owned && owned.count > 1 &&
                                    <em className="sh-stash__x">×{owned.count}</em>}
                            </span>
                            <span className="sh-stash__n">{slot.name}</span>
                            {!owned && slot.requirement &&
                                <span className="sh-stash__req">{slot.requirement}</span>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SurfaceBadgeStash;
